import PropTypes from 'prop-types';
import { useCallback } from 'react';

import { trackAction } from '@ctct/distui-helpers';
import { LoadingIndicator } from '@ctct/ui-react';

import ComparisonTableLegacyPlans from './ComparisonTableLegacyPlans';
import ComparisonTableV21 from './ComparisonTableV21';
import { useSmsEligibility } from '../hooks/useSmsEligibility';
import { useUpgradeContext } from '../UpgradeContext';
import {
  getHideSmsInfo,
  getIsAuCustomer,
  getIsGbCustomer,
  isSmsAddonInCatalog,
} from '../utils/comparisonTableSmsVisibility';

const ComparisonTableStrategy = ({ isLegacy }) => {
  const [
    {
      upgradeInfo,
      selectedPlanIdFromCompare,
      comparePlanBlocks,
      payingForPlan,
      isSingleBill,
      allAddOns,
      upgradeFormattedCustomerInfo,
      smsStartingPrice,
    },
    dispatchUpgrade,
  ] = useUpgradeContext();

  const planGroupId = upgradeInfo?.currentPlanGroupId ?? upgradeInfo?.planGroupId;
  const countryCode = upgradeFormattedCustomerInfo?.country;
  const isPlanDataReady = Boolean(comparePlanBlocks?.length);

  // Defer eligibility until getAddOns has run in useInitPlanBlocks so add-on catalog loads first.
  const smsEligibility = useSmsEligibility(countryCode, { enabled: isPlanDataReady });

  const isAuCustomer = getIsAuCustomer({ planGroupId, countryCode });
  const isGbCustomer = getIsGbCustomer({ planGroupId, countryCode });

  // Catalog check matches any CTCT-SMS-ADDON* id from getAddOns. Bronze-plan pricing
  // can resolve even when the current-plan getAddOns response omits SMS tiers.
  const smsAddonAvailable = isSmsAddonInCatalog(allAddOns) || smsStartingPrice != null;
  const hideSMSinfo = getHideSmsInfo({
    isSmsEligible: smsEligibility.isEligible,
    smsAddonAvailable,
    isEligibilityLoading: smsEligibility.isLoading,
    eligibilityCheckFailed: smsEligibility.hasError,
  });

  const onPlanSelect = useCallback(
    (planId) => {
      dispatchUpgrade({ type: 'setSelectedPlanIdFromCompare', value: planId });
      trackAction({
        action: `Feature Upgrade Dialog, compare plans, clicked update plan to ${planId}`,
      });
    },
    [dispatchUpgrade]
  );

  if (!comparePlanBlocks?.length) return <LoadingIndicator />;

  const showDecimals = comparePlanBlocks.some(
    ({ planBlockInfo }) => !Number.isInteger(Number(planBlockInfo.planPrice))
  );

  if (isLegacy) {
    return <ComparisonTableLegacyPlans planBlocks={comparePlanBlocks} showDecimals={showDecimals} />;
  }

  return (
    <ComparisonTableV21
      planBlocks={comparePlanBlocks}
      disableAllButtons={payingForPlan}
      hideAllButtons={isSingleBill}
      selectedPlanId={selectedPlanIdFromCompare || upgradeInfo?.recommendedPlanId}
      onPlanSelect={onPlanSelect}
      hideSMSinfo={hideSMSinfo}
      showDecimals={showDecimals}
      isAuCustomer={isAuCustomer}
      isGbCustomer={isGbCustomer}
      smsStartingPrice={smsStartingPrice}
    />
  );
};

ComparisonTableStrategy.propTypes = {
  isLegacy: PropTypes.bool.isRequired,
};

export default ComparisonTableStrategy;
